import React, { useState, useEffect, useCallback } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { Link } from "react-router-dom";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { financeAuthPayload } from "../../components/finance/AccountSelect";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Depreciation Schedule (Phase B)
 *
 * Straight-line depreciation from the fixed asset register. The listing is
 * computed client-side for display; the authoritative per-asset schedule comes
 * from depreciation/schedule and posting is per-asset via depreciation/post
 * ({ asset_id, period_date, amount }). The Depreciation Expense / Accumulated
 * Depreciation accounts are NOT chosen here — the backend reads them from GL
 * Settings (Finance Setup). Posting no-ops silently until those are set.
 */

const monthsBetween = (fromStr, toDate) => {
  if (!fromStr) return 0;
  const from = new Date(fromStr);
  if (isNaN(from)) return 0;
  return Math.max(
    0,
    (toDate.getFullYear() - from.getFullYear()) * 12 +
      (toDate.getMonth() - from.getMonth())
  );
};

const usefulLife = (r) =>
  Number(r.useful_life_years ?? r.asset_useful_life_years) || 0;

const Depreciation = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Post modal (per asset)
  const [showPost, setShowPost] = useState(false);
  const [postAsset, setPostAsset] = useState(null);
  const [postForm, setPostForm] = useState({ period_date: "", amount: "" });
  const [posting, setPosting] = useState(false);

  // Schedule modal (per asset)
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAsset, setScheduleAsset] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const money = (v) => `${customer_currency} ${formatNumber(v || 0)}`;

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.getassets,
        JSON.stringify({ ...financeAuthPayload(), limit: 500, page: 1 })
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Depreciation fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const now = new Date();

  const compute = (r) => {
    const cost = Number(r.asset_amount) || 0;
    const life = usefulLife(r);
    const annual =
      r.annual_depreciation != null
        ? Number(r.annual_depreciation)
        : life > 0
        ? cost / life
        : 0;
    const disposed =
      String(r.asset_status || "active").toLowerCase() === "disposed";
    let accumulated;
    if (r.accumulated_depreciation != null) {
      accumulated = Number(r.accumulated_depreciation);
    } else {
      const monthsElapsed = Math.min(monthsBetween(r.asset_date, now), life * 12);
      accumulated = Math.min((annual / 12) * monthsElapsed, cost);
    }
    if (disposed) accumulated = cost;
    return { cost, life, annual, accumulated, nbv: Math.max(0, cost - accumulated), disposed };
  };

  const computed = rows.map((r) => ({ ...r, _d: compute(r) }));
  const depreciable = computed.filter((r) => r._d.life > 0 && !r._d.disposed);
  const missingLife = computed.filter((r) => r._d.life <= 0 && !r._d.disposed).length;

  const totals = computed.reduce(
    (a, r) => {
      a.cost += r._d.cost;
      a.accumulated += r._d.accumulated;
      a.nbv += r._d.nbv;
      return a;
    },
    { cost: 0, accumulated: 0, nbv: 0 }
  );

  const openPost = (r) => {
    setPostAsset(r);
    setPostForm({
      period_date: new Date().toISOString().slice(0, 10),
      amount: r._d.annual ? String(Number(r._d.annual.toFixed(2))) : "",
    });
    setShowPost(true);
  };

  const submitPost = async () => {
    if (!postAsset) return;
    if (!postForm.period_date || !postForm.amount || Number(postForm.amount) <= 0) {
      toast.warning("Period date and a depreciation amount greater than zero are required.");
      return;
    }
    setPosting(true);
    try {
      const res = await simplePostCall(
        ApiConfig.depreciationPost,
        JSON.stringify({
          ...financeAuthPayload(),
          asset_id: postAsset.asset_id,
          period_date: postForm.period_date,
          amount: Number(postForm.amount),
        })
      );
      if (res?.result) {
        toast.success("Depreciation posted for the period.");
        setShowPost(false);
        fetchAssets();
      } else {
        toast.error(res?.message || "Failed to post depreciation.");
      }
    } catch (err) {
      toast.error("An error occurred while posting depreciation.");
    } finally {
      setPosting(false);
    }
  };

  const openSchedule = async (r) => {
    setScheduleAsset(r);
    setScheduleData(null);
    setShowSchedule(true);
    setScheduleLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.depreciationSchedule,
        JSON.stringify({ ...financeAuthPayload(), asset_id: r.asset_id })
      );
      if (res?.result) {
        setScheduleData(res.data || {});
      } else {
        toast.error(res?.message || "No schedule available.");
      }
    } catch (err) {
      toast.error("Failed to load depreciation schedule.");
    } finally {
      setScheduleLoading(false);
    }
  };

  return (
    <div className="finance-page-container">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">Depreciation Schedule</h4>
        <div className="action-buttons-group">
          <Link to="/accountant/finance/setup">
            <Button variant="outline-secondary">Finance Setup</Button>
          </Link>
        </div>
      </div>

      <div className="yc-list-body">
        <div className="fin-stat-row">
          <div className="fin-stat-card fin-accent-asset">
            <span className="fin-stat-label">Total Cost</span>
            <span className="fin-stat-value">{money(totals.cost)}</span>
          </div>
          <div className="fin-stat-card fin-accent-expense">
            <span className="fin-stat-label">Accumulated Depreciation</span>
            <span className="fin-stat-value">{money(totals.accumulated)}</span>
          </div>
          <div className="fin-stat-card fin-accent-income">
            <span className="fin-stat-label">Net Book Value</span>
            <span className="fin-stat-value">{money(totals.nbv)}</span>
          </div>
          <div className="fin-stat-card fin-accent-all">
            <span className="fin-stat-label">Depreciable Assets</span>
            <span className="fin-stat-value">{depreciable.length}</span>
          </div>
        </div>

        <div className="fin-inline-warning" style={{ margin: "0 20px 12px" }}>
          Accumulated depreciation and net book value below are straight-line{" "}
          <strong>estimates</strong>; each asset's posted figures are in its{" "}
          <strong>Schedule</strong>. Posting uses the{" "}
          <strong>Depreciation Expense</strong> and{" "}
          <strong>Accumulated Depreciation</strong> accounts configured in{" "}
          <Link to="/accountant/finance/setup">Finance Setup</Link>. If they are
          not set, posting silently does nothing.
          {missingLife > 0 && (
            <>
              {" "}
              {missingLife} asset{missingLife === 1 ? "" : "s"} have no useful
              life set and are excluded.
            </>
          )}
        </div>

        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-8">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Asset</div>
            <div className="yc-list-cell">Cost</div>
            <div className="yc-list-cell">Useful Life</div>
            <div className="yc-list-cell">Annual Depr.</div>
            <div className="yc-list-cell">Accumulated</div>
            <div className="yc-list-cell">Net Book Value</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : computed.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">No assets to depreciate</span>
            <span className="fin-empty-text">
              The depreciation schedule is built from the fixed asset register.
              Add assets with a cost and useful life to see straight-line
              depreciation here.
            </span>
          </div>
        ) : (
          <div className="yc-list-row-list">
            {computed.map((r, idx) => {
              const d = r._d;
              return (
                <div key={r.asset_id || idx} className="yc-list-row-card">
                  <div className="yc-list-grid yc-list-grid-8">
                    <div className="yc-list-cell">{idx + 1}</div>
                    <div className="yc-list-cell fw-semibold">{r.asset_name}</div>
                    <div className="yc-list-cell">{money(d.cost)}</div>
                    <div className="yc-list-cell">
                      {d.life > 0 ? `${d.life} yrs` : "—"}
                    </div>
                    <div className="yc-list-cell">
                      {d.life > 0 ? money(d.annual) : "—"}
                    </div>
                    <div className="yc-list-cell">{money(d.accumulated)}</div>
                    <div className="yc-list-cell fw-semibold">{money(d.nbv)}</div>
                    <div className="yc-list-cell yc-list-cell-actions">
                      {!d.disposed && d.life > 0 && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            onClick={() => openSchedule(r)}
                          >
                            Schedule
                          </Button>
                          <Button
                            size="sm"
                            className="btn btn-primary"
                            onClick={() => openPost(r)}
                          >
                            Post
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Post depreciation modal (per asset) */}
      <Modal show={showPost} onHide={() => !posting && setShowPost(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Post Depreciation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {postAsset && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div>
                <b>{postAsset.asset_name}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Cost {money(postAsset._d.cost)} • Net book value{" "}
                {money(postAsset._d.nbv)}
              </div>
            </div>
          )}
          <div className="fin-inline-warning">
            Posts Dr Depreciation Expense, Cr Accumulated Depreciation using the
            accounts from Finance Setup. Rejected if it would depreciate past the
            asset's original cost.
          </div>
          <Form.Group className="mb-3">
            <Form.Label>
              Period (posting date) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={postForm.period_date}
              onChange={(e) =>
                setPostForm((p) => ({ ...p, period_date: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              Depreciation Amount ({customer_currency}){" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={postForm.amount}
              onChange={(e) =>
                setPostForm((p) => ({ ...p, amount: e.target.value }))
              }
            />
            <Form.Text className="text-muted">
              Defaults to one year of straight-line depreciation.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPost(false)}
            disabled={posting}
          >
            Cancel
          </Button>
          <Button className="btn btn-primary" onClick={submitPost} disabled={posting}>
            {posting ? <Spinner size="sm" /> : "Post Depreciation"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Schedule modal (per asset) */}
      <Modal
        show={showSchedule}
        onHide={() => setShowSchedule(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Depreciation Schedule
            {scheduleAsset ? ` — ${scheduleAsset.asset_name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduleLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : !scheduleData ? (
            <div className="text-muted text-center py-3">
              No schedule available.
            </div>
          ) : (
            <>
              <div className="d-flex gap-4 mb-3 flex-wrap">
                <div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Posted Accumulated
                  </div>
                  <div className="fw-bold">
                    {money(scheduleData.posted_accumulated_depreciation)}
                  </div>
                </div>
                <div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Posted Net Book Value
                  </div>
                  <div className="fw-bold">
                    {money(scheduleData.posted_net_book_value)}
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="lib-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Date</th>
                      <th>Depreciation</th>
                      <th>Accumulated</th>
                      <th>Net Book Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(scheduleData.projected_schedule || []).map((p, i) => (
                      <tr key={p.period || i}>
                        <td>{p.period || i + 1}</td>
                        <td>
                          {p.period_date
                            ? new Date(p.period_date).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>{money(p.depreciation_amount)}</td>
                        <td>{money(p.accumulated_depreciation)}</td>
                        <td className="fw-semibold">{money(p.net_book_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSchedule(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Depreciation;
